<script setup lang="ts">
import type { Component } from 'vue';
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import theme from './index';
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue';

type ButtonType = 'button' | 'submit' | 'reset';
type Color = 'primary' | 'danger' | 'warning' | 'success' | 'neutral';
type Variant = 'solid' | 'link' | 'ghost' | 'outline';
type Size = 'xs' | 'sm' | 'md' | 'lg';
type IconPos = 'left' | 'right';

defineOptions({
    inheritAttrs: false,
});

const props = withDefaults(defineProps<{
    label?: string;
    /**
     * HTML button type attribute
     * @default 'button'
     */
    type?: ButtonType;
    /**
     * Button color variant
     * @values 'primary', 'danger', 'warning', 'success', 'neutral'
     * @default 'primary'
     */
    color?: Color;
    /**
     * Button style variant
     * @values 'solid', 'link', 'ghost', 'outline'
     * @default 'solid'
     */
    variant?: Variant;
    /**
     * Button size
     * @values 'xs', 'sm', 'md', 'lg'
     * @default 'md'
     */
    size?: Size;
    /**
     * Position of the icon relative to the label
     * @values 'left', 'right'
     * @default 'left'
     */
    iconPos?: IconPos;
    /**
     * Render the button with equal padding on all sides
     * @default false
     */
    square?: boolean;
    /**
     * Render the button full width
     * @default false
     */
    block?: boolean;
    /**
     * Icon component from @tabler/icons-vue or similar
     * Displayed to the left of the button text
     */
    icon?: Component;
    /**
     * Show loading spinner overlay and disable the button
     * When true, the button becomes non-interactive and displays a centered spinner
     * @default false
     */
    loading?: boolean;
    /**
     * Disable the button
     * When true, the button becomes non-interactive
     * @default false
     */
    disabled?: boolean;
    /**
     * URL to navigate to
     * When provided, renders as an Inertia Link or <a> tag instead of button
     */
    href?: string;
    /**
     * Render as external link (<a> tag) instead of Inertia Link
     * Only applies when href is provided
     * @default false
     */
    external?: boolean;
}>(), {
    label: undefined,
    type: 'button',
    color: 'primary',
    variant: 'solid',
    size: 'md',
    iconPos: 'left',
    square: false,
    block: false,
    icon: undefined,
    loading: false,
    disabled: false,
    href: undefined,
    external: false,
});

const isButtonDisabled = computed(() => props.disabled || props.loading);

const componentType = computed(() => {
    if (!props.href) {
        return 'button';
    }

    if (props.external) {
        return 'a';
    }

    return Link;
});

const ui = computed(() => {
    return theme({
        color: props.color,
        variant: props.variant,
        size: props.size,
        square: props.square,
        block: props.block,
        loading: props.loading,
        disabled: isButtonDisabled.value,
        iconPos: props.iconPos,
    });
});

const buttonType = computed(() => {
    return props.href ? undefined : props.type;
});

const isDisabledAttribute = computed(() => {
    return props.href ? undefined : isButtonDisabled.value;
});

const ariaDisabled = computed(() => {
    return props.href && isButtonDisabled.value ? 'true' : undefined;
});

const handleClick = (event: Event) => {
    if (props.href && isButtonDisabled.value) {
        event.preventDefault();
        event.stopPropagation();
    }
};
</script>

<template>
    <component
        :is="componentType"
        v-bind="$attrs"
        :href="href"
        :type="buttonType"
        :disabled="isDisabledAttribute"
        :aria-disabled="ariaDisabled"
        :class="ui.base()"
        :aria-busy="loading"
        @click="handleClick"
    >
        <span
            v-if="loading"
            :class="ui.spinner()"
        >
            <SpinnerIcon :class="ui.spinnerIcon()" />
        </span>

        <span :class="ui.label()">
            <component
                :is="icon"
                v-if="icon"
                :class="ui.icon()"
            />

            <slot>
                {{ label }}
            </slot>
        </span>
    </component>
</template>
